import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookmarkCheck, Check, MapPinned, Route } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useExplore } from "@/lib/ExploreContext";
import { useSaved } from "@/lib/SavedContext";
import { listingPath } from "@/lib/businessSlug";

// Real, independent venues in Santurce. This is a suggested sequence,
// not a booked tour, route-time guarantee or assertion that all are open.
const STOPS = [
  { name:"Museo de Arte Contemporáneo de Puerto Rico", en:"START WITH ART", es:"COMIENZA CON ARTE", detailEn:"Contemporary art in Santurce", detailEs:"Arte contemporáneo en Santurce" },
  { name:"Galería Petrus", en:"KEEP EXPLORING", es:"SIGUE EXPLORANDO", detailEn:"A local gallery stop", detailEs:"Una parada en la galería" },
  { name:"Machete", en:"FINISH AFTER DARK", es:"TERMINA DE NOCHE", detailEn:"Drinks on Calle Cerra", detailEs:"Cocteles en la calle Cerra" },
];
const photoOf = (item) => item.storefront_image || item.cover_image || item.gallery_images?.find(Boolean);

export default function HomeMakeADay() {
  const { lang } = useLang();
  const { allListings } = useExplore();
  const { saved, toggleSave } = useSaved();
  const es = lang === "es";
  const stops = useMemo(() => STOPS.map((spec) => {
    const place = allListings.find((item) => item.name?.trim() === spec.name && item.status === "Published" && !item.coming_soon);
    return place ? { ...spec, place } : null;
  }).filter(Boolean), [allListings]);
  if (stops.length !== STOPS.length) return null;

  const allSaved = stops.every(({place}) => saved.has(String(place.id)));
  const saveStops = () => {
    stops.forEach(({place}) => {
      if (!saved.has(String(place.id))) toggleSave(place.id);
    });
  };
  const previewPath = "/planner?list=" + stops.map(({place}) => encodeURIComponent(place.id)).join(",");
  return (
    <section aria-labelledby="home-itinerary-title" className="mx-auto mt-8 max-w-[1240px] px-4 sm:mt-12 sm:px-6 lg:px-8">
      <div className="border-t-2 border-[#191919] pt-3 dark:border-[#f8f5ee]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-[.17em] text-[#ab4538] dark:text-[#ef9780]">
              {es ? "GUÍA LOCAL / UNA TARDE EN SANTURCE" : "LOCAL EDIT / ONE AFTERNOON IN SANTURCE"}
            </p>
            <h2 id="home-itinerary-title" className="mt-1.5 text-[clamp(26px,4.5vw,44px)] font-black uppercase leading-[.96] tracking-[-.06em] text-[#191919] dark:text-[#f5f2eb]">
              {es ? "HAZ UN DÍA DE ESTO." : "MAKE A DAY OF IT."}
            </h2>
            <p className="mt-2 max-w-[58ch] text-[12px] leading-[1.5] text-[#77736b] dark:text-[#bcb4a9]">
              {es ? "Tres paradas reales. Arte, una galería y la noche en Santurce." : "Three real local stops. Art, a gallery, and a little after-dark Santurce."}
            </p>
          </div>
          <span className="hidden items-center gap-1.5 font-mono text-[10px] font-bold text-[#837e72] dark:text-[#c8bfb4] sm:inline-flex"><Route className="h-4 w-4" /> 03 {es ? "PARADAS" : "STOPS"}</span>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-3 sm:gap-3">
          {stops.map(({place, ...spec}, i) => (
            <Link key={place.id} to={listingPath(place)}
              className="group flex min-h-[102px] items-stretch overflow-hidden border border-[#d3cec5] bg-[#f6f2eb] text-[#191919] transition-colors hover:border-[#83796d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b54031] dark:border-white/20 dark:bg-[#272522] dark:text-white sm:block">
              <div className="h-[102px] w-[118px] shrink-0 overflow-hidden bg-[#37352d] sm:h-[160px] sm:w-full">
                {photoOf(place) ? <img src={photoOf(place)} alt="" loading="lazy" decoding="async"
                  onError={(e)=>{e.currentTarget.style.display="none";}}
                  className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.05]" />
                  : <span className="flex h-full w-full items-center justify-center font-mono text-[11px] font-black text-white/65">YTS / PR</span>}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-3 sm:min-h-[125px]">
                <span className="font-mono text-[9px] font-black tracking-[.09em] text-[#a64436] dark:text-[#eeab97]">{String(i+1).padStart(2,"0")} / {es ? spec.es : spec.en}</span>
                <span className="line-clamp-2 text-[14px] font-black leading-[1.18] tracking-[-.025em] sm:text-[17px]">{place.name}</span>
                <span className="flex items-center justify-between gap-2 text-[11px] leading-[1.4] text-[#736d62] dark:text-white/65">
                  {es ? spec.detailEs : spec.detailEn}<ArrowUpRight className="h-4 w-4 shrink-0 text-[#191919] dark:text-white" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-b border-[#c9c2b8] pb-3 dark:border-white/20">
          <p className="max-w-[50ch] text-[10px] leading-[1.5] text-[#7b746a] dark:text-white/60">
            {es ? "Ruta sugerida, no tour reservado. Confirma horarios, disponibilidad y transporte antes de salir." : "A suggested route, not a booked tour. Confirm opening hours, availability and travel before visiting."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={saveStops} disabled={allSaved}
              className="inline-flex min-h-10 items-center gap-2 border border-[#1c1c1b] px-3 text-[10px] font-black uppercase tracking-[.03em] text-[#1c1c1b] transition hover:bg-[#1c1c1b] hover:text-white disabled:opacity-60 dark:border-white/50 dark:text-white dark:hover:bg-white dark:hover:text-black">
              {allSaved ? <Check className="h-4 w-4"/> : <BookmarkCheck className="h-4 w-4"/>}
              {allSaved ? (es ? "Guardado" : "Saved to My List") : (es ? "Guardar paradas" : "Save all 3 stops")}
            </button>
            <Link to={previewPath} className="inline-flex min-h-10 items-center gap-2 bg-[#191919] px-3 text-[10px] font-black uppercase tracking-[.03em] text-white transition hover:bg-[#b43d2c]">
              <MapPinned className="h-4 w-4"/>{es ? "Ver las paradas" : "View the stops"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
